import React, { useState } from 'react';
import { useVersionCheck } from '@/hooks';

/**
 * Version Debug Panel
 * Shows version info - useful for mobile testing where console isn't available
 * Only visible when URL has ?debug=true
 */
export default function VersionDebugPanel() {
  const { currentVersion, latestVersion, updateAvailable, checkForUpdates, refreshApp } = useVersionCheck();
  const [isOpen, setIsOpen] = useState(false);

  // Only show if ?debug=true in URL
  const urlParams = new URLSearchParams(window.location.search);
  const debugMode = urlParams.get('debug') === 'true';

  if (!debugMode) return null;

  return (
    <>
      {/* Floating debug button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-4 right-4 z-[9998] btn btn-circle btn-sm btn-neutral opacity-50 hover:opacity-100"
        title="Version Debug"
      >
        🔍
      </button>

      {/* Debug panel */}
      {isOpen && (
        <div className="fixed bottom-16 right-4 z-[9999] card bg-base-100 shadow-xl w-80 max-w-[90vw]">
          <div className="card-body p-4">
            <h3 className="card-title text-sm">Version Debug</h3>

            <div className="space-y-2 text-xs">
              {/* Current Version */}
              <div>
                <div className="font-bold opacity-70">Stored Version:</div>
                <div className="font-mono bg-base-200 p-2 rounded mt-1 break-all">
                  {currentVersion || 'Not set'}
                </div>
              </div>

              {/* Latest Version */}
              <div>
                <div className="font-bold opacity-70">HTML Version:</div>
                <div className="font-mono bg-base-200 p-2 rounded mt-1 break-all">
                  {latestVersion || 'Not found'}
                </div>
              </div>

              {/* Status */}
              <div>
                <div className="font-bold opacity-70">Update Status:</div>
                <div className={`badge ${updateAvailable ? 'badge-warning' : 'badge-success'} mt-1`}>
                  {updateAvailable ? '⚠️ Update Available' : '✅ Up to Date'}
                </div>
              </div>

              {/* localStorage Status */}
              <div>
                <div className="font-bold opacity-70">localStorage:</div>
                <div className={`badge ${checkLocalStorage() ? 'badge-success' : 'badge-error'} mt-1`}>
                  {checkLocalStorage() ? '✅ Working' : '❌ Unavailable'}
                </div>
              </div>

              {/* Service Worker Status */}
              <div>
                <div className="font-bold opacity-70">Service Worker:</div>
                <div className="badge badge-info mt-1">
                  {'serviceWorker' in navigator ? '✅ Supported' : '❌ Not Supported'}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={checkForUpdates}
                  className="btn btn-xs btn-primary flex-1"
                >
                  Check Now
                </button>
                <button
                  onClick={refreshApp}
                  className="btn btn-xs btn-warning flex-1"
                >
                  Force Refresh
                </button>
              </div>

              {/* Clear Cache */}
              <button
                onClick={clearAllCaches}
                className="btn btn-xs btn-error w-full"
              >
                Clear All Caches
              </button>
            </div>

            <div className="text-xs opacity-50 mt-2">
              💡 Add ?debug=true to URL to see this panel
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function checkLocalStorage() {
  try {
    localStorage.setItem('test', 'test');
    localStorage.removeItem('test');
    return true;
  } catch {
    return false;
  }
}

function clearAllCaches() {
  if (!confirm('Clear all caches? This will reload the page.')) return;

  // Clear localStorage
  try {
    localStorage.clear();
  } catch (e) {
    console.warn('Failed to clear localStorage:', e);
  }

  // Clear sessionStorage
  try {
    sessionStorage.clear();
  } catch (e) {
    console.warn('Failed to clear sessionStorage:', e);
  }

  // Unregister service workers
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then(registrations => {
      registrations.forEach(registration => registration.unregister());
    });
  }

  // Clear caches
  if ('caches' in window) {
    caches.keys().then(names => {
      names.forEach(name => caches.delete(name));
    });
  }

  // Reload
  setTimeout(() => window.location.reload(), 500);
}
